import React, { useEffect, useMemo, useState, memo } from "react";
import { Tables, Typography, showToast } from "@deeptrue-frontend/ui";
import Search from "components/Fields/Search";
import api from "api";
import { UserType } from "pages/OrganizationAdmin/Users";
import User from "./User";
import { ProductType, UserShared } from "pages/OrganizationAdmin/types";
import { useFormatMessage } from "components/IntlProvider";

const { Table, TableBody, TableHeader } = Tables;

interface Props {
  productData: ProductType;
  isLoaded: boolean;
  organizationCodename: string;
  refreshProduct: () => void;
}

const Users = memo(
  ({ productData, organizationCodename, isLoaded, refreshProduct }: Props) => {
    const [searchedUsers, setSearchedUsers] = useState<UserType[]>([]);
    const [productUsers, setProductUsers] = useState<UserShared[]>([]);
    const t = useFormatMessage();

    const getUsers = (searchValue?: string) => {
      return api.resources.organization.users
        .getOrganizationUsers({
          organizationCodename: organizationCodename,
          params: {
            search: searchValue,
          },
        })
        .then(({ data }) => {
          setSearchedUsers(data.results);
        });
    };

    useEffect(() => {
      getUsers();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setProductUsers(productData.shared_to_users);
    }, [productData.shared_to_users]);

    const addUserToProduct = (id: number) => {
      const users = [
        ...productData.shared_to_users.map((user: UserShared) => user.id),
        id,
      ];

      return api.resources.organization.products
        .updateProduct({
          organizationCodename: organizationCodename,
          id: productData.id as number,
          data: {
            shared_to_users: users,
          },
        })
        .then(() => {
          refreshProduct();
          showToast({
            type: "success",
            title: t("organization.addedTo", "{x} added to {y}", {
              x: t("organization.user", "User"),
              y: t("organization.product", "Product"),
            }),
          });
        });
    };

    const removeUserfromProduct = (id: number) => {
      const users = productData.shared_to_users.filter(
        (user: UserShared) => user.id !== id
      );

      return api.resources.organization.products
        .updateProduct({
          organizationCodename: organizationCodename,
          id: productData.id as number,
          data: {
            shared_to_users: users.map((user: UserShared) => user.id),
          },
        })
        .then(() => {
          refreshProduct();
          showToast({
            type: "success",
            title: t("organization.removedFrom", "{x} removed from {y}", {
              x: t("organization.user", "User"),
              y: t("organization.product", "Product"),
            }),
          });
        });
    };

    const options = searchedUsers.map((userData: UserType) => ({
      id: userData.user.id,
      label: `${userData.user.first_name} ${userData.user.last_name} (${userData.user.email})`,
    }));

    const filteredOptions = useMemo(() => {
      return options.filter((searchedUser: { id: number; label: string }) => {
        const itemIndex = productUsers.findIndex((user) => {
          return user.id === searchedUser.id;
        });

        return itemIndex === -1;
      });
    }, [productUsers, options]);

    return (
      <>
        <Typography fontWeight={600} mb="1rem">
          {t("organization.addTo", "Add {x} to {y}", {
            y: t("organization.product", "Product"),
            x: t("organization.users", "Users"),
          })}
        </Typography>
        <Search
          options={filteredOptions.map(({ id, label }) => ({
            value: id,
            label,
          }))}
          styles={{
            maxWidth: "450px",
          }}
          name="user"
          onChange={(_name, user) => {
            if (user) {
              addUserToProduct(user.value);
            }
          }}
          placeholder={t("organization.searchValue", "Search {name}", {
            name: t("organization.users", "users"),
          })}
          asyncSeach={(searchedValue: string) => getUsers(searchedValue)}
        />
        <Typography fontWeight={600} mb="1rem" mt="2rem">
          {t("organization.sharedWith", "{x} shared with {y}", {
            x: t("organization.product", "Product"),
            y: t("organization.users", "Users"),
          })}
          :
        </Typography>
        <Table>
          <TableHeader
            cells={[
              {
                text: t("organization.columns.name", "Name"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: "Email",
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.resources", "Resources"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
            ]}
          />

          <TableBody
            isLoading={!isLoaded}
            withLoader
            isData={productData.shared_to_users.length > 0}
            noDataText={t(
              "noresults",
              "No results. We have nothing to show here."
            )}
          >
            {productUsers.map((user: UserShared) => {
              return (
                <User
                  id={user.id}
                  displayName={`${user.first_name} ${user.last_name}`}
                  email={user.email}
                  key={user.email}
                  removeUser={removeUserfromProduct}
                />
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }
);

export default Users;
